const MARKETING_PARAM_PREFIX = 'utm_';
const MARKETING_PARAM_NAMES = new Set(['fbclid', 'gclid']);

export interface NormalizedLegacyUrl {
  host: string;
  path: string;
  query: string;
  key: string;
}

export interface LegacyUrlParts {
  host: string;
  path: string;
  query?: string;
}

function shouldIgnoreQueryParam(name: string) {
  const normalizedName = name.toLowerCase();

  return normalizedName.startsWith(MARKETING_PARAM_PREFIX) || MARKETING_PARAM_NAMES.has(normalizedName);
}

function normalizePath(pathname: string) {
  if (pathname === '/') {
    return pathname;
  }

  return pathname.endsWith('/') ? pathname : `${pathname}/`;
}

function normalizeHost(url: URL) {
  const host = url.hostname.toLowerCase();
  const isDefaultPort = (url.protocol === 'http:' && url.port === '80') || (url.protocol === 'https:' && url.port === '443');

  if (!url.port || isDefaultPort) {
    return host;
  }

  return `${host}:${url.port}`;
}

function normalizeQuery(url: URL) {
  const entries = [...url.searchParams.entries()]
    .filter(([name]) => !shouldIgnoreQueryParam(name))
    .sort(([leftName, leftValue], [rightName, rightValue]) => {
      if (leftName === rightName) {
        if (leftValue < rightValue) {
          return -1;
        }

        if (leftValue > rightValue) {
          return 1;
        }

        return 0;
      }

      if (leftName < rightName) {
        return -1;
      }

      if (leftName > rightName) {
        return 1;
      }

      return 0;
    });

  return new URLSearchParams(entries).toString();
}

export function normalizeLegacyUrl(urlLike: string | URL): NormalizedLegacyUrl {
  const url = urlLike instanceof URL ? urlLike : new URL(urlLike);
  const host = normalizeHost(url);
  const path = normalizePath(url.pathname);
  const query = normalizeQuery(url);
  const key = query ? `${host}${path}?${query}` : `${host}${path}`;

  return {
    host,
    path,
    query,
    key,
  };
}

export function normalizeLegacyUrlParts({ host, path, query = '' }: LegacyUrlParts): NormalizedLegacyUrl {
  const normalizedUrl = new URL(`https://${host}${path.startsWith('/') ? path : `/${path}`}`);

  if (query) {
    normalizedUrl.search = query;
  }

  return normalizeLegacyUrl(normalizedUrl);
}
